<script lang="ts">
    import { get } from 'svelte/store';
    import { onMount, afterUpdate } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { z } from 'zod';

    import type { VehicleResponseType } from './types';
    import { api, customerSettings } from '../../../stores/api';
    import IconExpander from '../../shared/Icons/IconExpander.svelte';
    import ButtonUpdate from '../../shared/ButtonUpdate.svelte';
    import Switch from '../../shared/Switch.svelte';
    import { type VehicleGroupResponseType, VehiclesApi } from './VehiclesApi';
    import InputText from '../../shared/InputText.svelte';
    import InputNumber from '../../shared/InputNumber.svelte';
    import Checkbox from '../../shared/Checkbox/Checkbox.svelte';
    import SearchForm from '../../shared/SearchForm.svelte';
    import SelectOpenHours from './SelectOpenHours.svelte';
    import ButtonRemove from '../../shared/ButtonRemove.svelte';
    import SelectWarehouse from '../Warehouses/SelectWarehouse.svelte';
    import type { WarehouseResponseType } from '../Warehouses/WarehouseApi';
    import InputEmail from '../../shared/InputEmail.svelte';
    import MessageDangerSmall from '../../shared/Messages/MessageDangerSmall.svelte';
    import ButtonSmallBlack from '../../shared/ButtonSmallBlack.svelte';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import SuccessInline from '../../shared/SuccessInline.svelte';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import { openModal } from 'svelte-modals';
    import ModalRemoveSomething from '../Modals/ModalRemoveSomething.svelte';
    import ErrorColor from '../../shared/ErrorColor.svelte';
    import IconMarkEmailUnread from '../../shared/Icons/IconMarkEmailUnread.svelte';
    import VehicleOneSection from './VehicleOneSection.svelte';
    import VehicleOneLabel from './VehicleOneLabel.svelte';
    import VehicleOneValue from './VehicleOneValue.svelte';
    import VehicleOneParagraph from './VehicleOneParagraph.svelte';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import LinkSmallBlack from '../../shared/LinkSmallBlack.svelte';
    import VehicleIcon from './VehicleIcon.svelte';
    import { Vehicle } from './Vehicle';
    import { VehicleExtremum, VehicleValidator } from './VehicleValidator';
    import ErrorBounduary from '../Jobs/ErrorBounduary.svelte';
    import VehicleRanking from './VehicleRanking.svelte';
    import TelematicsSliderSingle from '../TelematicsSlider/TelematicsSliderSingle.svelte';
    import ColorSymbol from '../../shared/ColorSymbol.svelte';
    import Textarea from '../../shared/Textarea.svelte';
    import Label from '../../shared/Label.svelte';
    import { debug } from '../../../stores/store';
    import RadioButton from '../../shared/RadioButton.svelte';
    import MultiSelect2 from '../../shared/MultiSelect2.svelte';
    import { vehiclesManager } from './VehiclesApp/vehiclesManager';
    import { features } from '../../shared/Features/features';
    import ExtendedSelect from '../../shared/ExtendedSelect.svelte';
    import { Umami } from '../../utilities/Umami';

    export let vehicle: VehicleResponseType;
    export let available_vehicle_options: string[];
    export let isNew: boolean;
    export let onUpdateOne: (instant: boolean) => void;
    export let showHeader: boolean = false;
    export let warehouses: WarehouseResponseType[] = [];
    export let error: string = '';
    export let expandedIndex: number = -1;
    export let index: number;
    export let addMode: boolean = false;
    export let selectMode: string = 'click';
    export let vehicleGroups: VehicleGroupResponseType[] = [];

    let vehicleGroupItems = vehicleGroups.map(g => ({
        id: g.group_id.toString(),
        label: g.group_name,
    }));
    let selectedVehicleGroupItems: { id: string; label: string }[] = [];

    let selectedVehiclesInGroupItems: { id: string; label: string }[] = [];

    // export let vehicleItems: { id: string; label: string }[] = [];

    let expanded: boolean = false;
    export let expandedMore: boolean = false;
    let warehouses_items: { id: string; label: string }[] = [];
    let vehicleOriginal: VehicleResponseType;
    let loading: boolean = false;
    let updateStatus: { success: boolean; text: string } = {
        success: false,
        text: '',
    };

    let warehouseName: string = '';
    let vehicleClass: Vehicle = new Vehicle();
    let validated: boolean = false;
    let showDescriptionField: boolean | null = null;

    //vehicleOriginal = {...vehicle};
    vehicleOriginal = JSON.parse(JSON.stringify(vehicle));

    if (isNew) {
        expandedIndex = index;
        expanded = true;
    }

    function update(vehicleClass: Vehicle) {
        if (validate(vehicleClass)) {
            let {
                email,
                enabled,
                track,
                open_hours,
                warehouse_id,
                webuser_id,
                route_optimize,
                route_end,
                truck_attributes,
                name,
                weight_limit,
                parcel_limit,
                open_hours_adjust_percent,
                color,
                description,
                ranking_percent,
                license_plate_number,
                is_group,
                route_cost,
                ..._
            } = vehicle;
            /*if (!route_end.coordinates) {
                route_end = null;
            }*/
            if (isNew) {
                VehiclesApi.createOne(get(api), {
                    email,
                    enabled,
                    track,
                    open_hours,
                    warehouse_id,
                    webuser_id,
                    route_optimize,
                    route_end,
                    truck_attributes,
                    name,
                    weight_limit,
                    parcel_limit,
                    last_position: null,
                    open_hours_adjust_percent,
                    color,
                    description,
                    ranking_percent,
                    license_plate_number,
                    is_group,
                    route_cost,
                })
                    .then(vehicle => {
                        Umami.event('vehicle_add');
                        onUpdateOne(true);
                    })
                    .catch(err => {
                        Umami.error('vehicle_add', err);
                        error = err;
                    });
            } else {
                if (vehicle.id) {
                    VehiclesApi.updateOne(get(api), vehicle.id, {
                        email,
                        enabled,
                        track,
                        open_hours,
                        warehouse_id,
                        webuser_id,
                        route_optimize,
                        route_end,
                        truck_attributes,
                        name,
                        weight_limit,
                        parcel_limit,
                        last_position: null,
                        open_hours_adjust_percent,
                        color,
                        description,
                        ranking_percent,
                        license_plate_number,
                        is_group,
                        route_cost,
                    })
                        .then(vehicle => {
                            Umami.event('vehicle_edit', { id: vehicle.id });
                            onUpdateOne(false);
                        })
                        .catch(err => {
                            Umami.error('vehicle_edit', err, { id: vehicle.id });
                            error = err;
                        });
                } else {
                    throw new Error('Vehicle id is not defined: ' + vehicle);
                }
            }
        }
    }

    function validate(vehicle: Vehicle) {
        return VehicleValidator.validateAll(vehicle);
    }

    let verifyEmailTimeout: any = null;
    let verifyEmailError: string = '';

    function verifyEmail(email: string) {
        VehiclesApi.verifyEmail(get(api), email)
            .then(result => {
                verifyEmailError = result.valid ? '' : $_('vehicle-email-error-' + result.reason);
                error = verifyEmailError;
            })
            .catch(err => {
                error = err;
            })
            .finally(() => {
                loading = false;
            });
    }

    function toggleExpanded() {
        expandedIndex = expandedIndex === index ? -1 : index;
    }

    function centerMap(coords: { x: number; y: number }) {
        // @ts-ignore
        Targeo.Base.Map.zoomAndCenter(coords, 20);
    }

    let mapObject: any = null;

    function mapObjectAdd() {
        if (vehicle.route_end) {
            let abbr = new Vehicle().set(vehicle).calcAbbreviation();
            mapObject = new Targeo.MapObject.Label(vehicle.route_end?.coordinates, abbr, {
                fs: 15,
                fc: '#000000',
                fm: { b: true },
                bw: 1,
                bc: '#000000',
                bs: 'solid',
                br: 4,
                p: [1, 3, 1, 3],
                bgc: '#98c8f8',
            });
            Targeo.Base.Map.UOAdd(mapObject);
        }
    }

    function mapObjectRemove() {
        if (mapObject) {
            Targeo.Base.Map.UORemove(mapObject.id);
            mapObject = null;
        }
    }

    // function handleInputNumberChange(obj: any, field: string, element: HTMLElement) {
    //     if (element) {
    //         let value = parseFloat(element.value);
    //         if (!isNaN(value) && value > 0) {
    //             obj[field] = value;
    //         } else {
    //             obj[field] = null;
    //             element.value = '';
    //         }
    //         if (!isNew) update()
    //     }
    // }

    function handleInputNumberChange(
        obj: any,
        field: string,
        element: HTMLInputElement | EventTarget | null,
        vehicleClass: Vehicle,
        nullable: boolean = false
    ) {
        if (element) {
            try {
                if (nullable && (<HTMLInputElement>element).value === '') {
                    obj[field] = null;
                    if (!isNew) update(vehicleClass);
                } else if (z.coerce.number().safeParse((<HTMLInputElement>element).value).success) {
                    obj[field] = z.coerce.number().parse((<HTMLInputElement>element).value);
                    if (!isNew) update(vehicleClass);
                }
            } catch (e) {
                console.warn(e);
            }
            // let value = parseFloat(element.value);
            // if (!isNaN(value) && value > 0) {
            //     obj[field] = value;
            //     if (!isNew) update()
            // } else {
            //     obj[field] = null;
            //     // element.value = '';
            // }
        }
    }

    function removeVehicleWithConfirm() {
        openModal(ModalRemoveSomething, {
            text: vehicle.is_group
                ? $_('Czy na pewno chcesz usunąć tę grupę pojazdów?')
                : $_('Are you sure you want to remove this vehicle?'),
            name: vehicle.name || '',
            onClick: () => {
                if (vehicle.id) {
                    loading = true;
                    VehiclesApi.removeOne(get(api), vehicle.id)
                        .then(vehicle => {
                            onUpdateOne(true);
                        })
                        .catch(err => {
                            Umami.error('vehicle_remove', err, { id: vehicle.id });
                            error = err;
                        })
                        .finally(() => {
                            Umami.event('vehicle_remove', { id: vehicle.id });
                            loading = false;
                        });
                } else {
                    throw new Error('Vehicle id is not defined: ' + vehicle);
                }
            },
        });
    }

    function getValidationTruckErrors(vehicle: Vehicle): string[] {
        let errors = [];
        if (!VehicleValidator.isTruckTotalWeightValid(vehicle)) {
            errors.push(
                $_('Vehicle_total_weight_error', {
                    values: {
                        min: VehicleExtremum.truckTotalWeight.min,
                        max: VehicleExtremum.truckTotalWeight.max,
                    },
                })
            );
        }
        if (!VehicleValidator.isTruckAxisLoadValid(vehicle)) {
            errors.push(
                $_('Vehicle_axis_load_error', {
                    values: {
                        min: VehicleExtremum.truckAxisLoad.min,
                        max: VehicleExtremum.truckAxisLoad.max,
                    },
                })
            );
        }
        if (!VehicleValidator.isTruckLengthValid(vehicle)) {
            errors.push(
                $_('Vehicle_length_error', {
                    values: {
                        min: VehicleExtremum.truckLength.min,
                        max: VehicleExtremum.truckLength.max,
                    },
                })
            );
        }
        if (!VehicleValidator.isTruckWidthValid(vehicle)) {
            errors.push(
                $_('Vehicle_width_error', {
                    values: {
                        min: VehicleExtremum.truckWidth.min,
                        max: VehicleExtremum.truckWidth.max,
                    },
                })
            );
        }
        if (!VehicleValidator.isTruckHeightValid(vehicle)) {
            errors.push(
                $_('Vehicle_height_error', {
                    values: {
                        min: VehicleExtremum.truckHeight.min,
                        max: VehicleExtremum.truckHeight.max,
                    },
                })
            );
        }
        return errors;
    }

    onMount(() => {
        $vehiclesManager.asyncGetItems(null).then(_ => {});
        warehouses_items = warehouses.map(warehouse => {
            return {
                id: (warehouse.id && warehouse.id.toString()) || '',
                label: warehouse.name,
            };
        });
    });

    afterUpdate(() => {
        if (expanded) {
            if (vehicle.route_end?.coordinates && !mapObject) {
                mapObjectAdd();
                centerMap(vehicle.route_end?.coordinates);
            }
        } else {
            mapObjectRemove();
        }
    });

    function onColorChange(event: Event) {
        if (event.target) {
            vehicle.color = (<HTMLInputElement>event.target).value;
            vehicle = vehicle;
            if (!isNew) update(vehicleClass);
            console.log('selected color: ', vehicle.color);
        }
    }

    let isDemoVehicle: boolean = false;

    $: expanded = expandedIndex === index;

    $: warehouseName = warehouses.find(w => w.id === vehicle.warehouse_id)?.name || '';

    $: {
        vehicleClass.set(vehicle);
        vehicleClass = vehicleClass;
        isDemoVehicle =
            (!vehicleClass.get()?.is_group && vehicleClass?.getEmail()?.endsWith('@automapa.pl')) ||
            vehicleClass?.getEmail()?.endsWith('@indigo.pl') ||
            false;
        validated = validate(vehicleClass);
    }

    $: {
        if (showDescriptionField === null) {
            showDescriptionField = vehicleClass.getDescription() !== '';
        }
    }

    $: {
        vehicleGroupItems = vehicleGroups.map(g => ({
            id: g.group_id.toString(),
            label: g.group_name,
        }));
        selectedVehicleGroupItems = vehicleGroups
            .filter(g => g.vehicle_ids.some(id => id === vehicle.id))
            .map(g => ({
                id: g.group_id.toString(),
                label: g.group_name,
            }));

        // list vehicles in this group (search vehicle_ids in vehicleGroups by vehicle.id)
        selectedVehiclesInGroupItems = vehicleGroups
            .filter(g => g.group_id === vehicle.id)
            .flatMap(g =>
                g.vehicle_ids.map(vid => {
                    let v = $vehiclesManager.getById(vid);
                    return { id: vid.toString(), label: v?.getName() || '[unknown]' };
                })
            );
    }
</script>

{#if showHeader}
    <div class="list-element vehicle-header list-element-header">
        <div class="vehicle_icon"></div>
        <div class="name">{$_('Vehicle_name')}</div>
        <div class="active"></div>
        <div class="email">{$_('Vehicle_email')}</div>
        <div class="warehouse">{$_('Warehouse')}</div>
    </div>
{/if}

{#if !expanded}
    <div
        class="list-element vehicle-header list-element-data"
        class:with-select-control={selectMode !== 'click'}
    >
        {#if !isNew}
            {#if selectMode !== 'click'}
                <div class="select_control">
                    {#if selectMode === 'radio'}
                        <RadioButton />
                    {:else}
                        <Checkbox />
                    {/if}
                </div>
            {/if}
            <div
                class="vehicle_icon"
                on:click={() => {
                    toggleExpanded();
                }}
            >
                <VehicleIcon vehicle={vehicleClass} scale={0.7} />
            </div>
            <div
                class="name"
                class:extended-background={expanded}
                on:click={() => {
                    toggleExpanded();
                }}
            >
                {(vehicle.name && vehicle.name) || ''}
            </div>
            <div class="active" class:extended-background={expanded}>
                {#if !vehicle.active && !isNew && !isDemoVehicle && !vehicle.is_group}
                    <ErrorColor>
                        <IconMarkEmailUnread scale={0.6} title={$_('vehicle_not_confirmed')} />
                    </ErrorColor>
                {/if}
                <ColorSymbol
                    color={vehicle.color ?? '#ffffff'}
                    opts={{}}
                    editable={true}
                    on:change={onColorChange}
                    scale={0.6}
                    type={null}
                    label=""
                />
            </div>
            <div
                class="email"
                class:extended-background={expanded}
                on:click={() => {
                    toggleExpanded();
                }}
                title={vehicle.email}
            >
                {#if isDemoVehicle}
                    <span style="opacity: 0.5">({$_('demo_account')})</span>
                {:else if vehicle.is_group}
                    <span style="opacity: 0.5">({$_('vehicle_group_label')})</span>
                {:else}
                    {(vehicle.email && vehicle.email) || '[email not set]'}
                {/if}
            </div>
            <div
                class="warehouse"
                class:extended-background={expanded}
                on:click={() => {
                    toggleExpanded();
                }}
            >
                {warehouseName}
            </div>
            <div class="expand-trigger">
                {#if !isNew}
                    <IconExpander
                        isOpen={expanded}
                        on:click={() => {
                            toggleExpanded();
                        }}
                    />
                {/if}
            </div>
        {/if}
    </div>
{/if}

{#if expanded}
    <div class="vehicle-expanded-container">
        <div class="vehicle-header">
            <div
                class="vehicle_icon"
                on:click={() => {
                    toggleExpanded();
                }}
            >
                <VehicleIcon vehicle={vehicleClass} scale={0.7} />
            </div>
            <div class="name">
                <div
                    style="display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start; height: 100%;"
                >
                    <InputText
                        value={(vehicle?.name && vehicle.name) || ''}
                        valid={vehicle?.name !== ''}
                        style="width: 100%"
                        on:change={event => {
                            if (event?.target) {
                                vehicle.name = event?.target.value;
                                vehicle = vehicle;
                                if (!isNew) update(vehicleClass);
                            }
                        }}
                    />
                </div>
            </div>
            <div
                class="active"
                style="display:flex; align-items:center; column-gap: 5px; justify-content: flex-end;"
            >
                <!-- do not show IconMarkEmailUnread when email has @automapa.pl at the end -->
                {#if !vehicle.active && !isNew && !isDemoVehicle}
                    <ErrorColor>
                        <IconMarkEmailUnread scale={0.6} title={$_('vehicle_not_confirmed')} />
                    </ErrorColor>
                {/if}
                <ColorSymbol
                    color={vehicle.color ?? '#ffffff'}
                    opts={{}}
                    editable={true}
                    on:change={onColorChange}
                    scale={0.6}
                    type={null}
                    label=""
                />
            </div>
            <div
                class="email"
                on:click={() => {
                    if (!isNew) toggleExpanded();
                }}
            >
                {#if isNew && !vehicle.is_group}
                    <div
                        style="display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start;  height: 100%;"
                    >
                        <!--                            className={(verifyEmailError ? 'input-error' : '')}-->
                        <InputText
                            value={vehicleClass.getEmail() ?? ''}
                            valid={verifyEmailError === '' &&
                                vehicle.email !== '' &&
                                vehicle.email !== null}
                            style="width: 100%"
                            on:keyup={event => {
                                /*
                                    verifyEmailError = VehicleValidator.isEmailAvailable(event?.target?.value, verifyEmailTimeout);
                                    error = verifyEmailError;
                                    if (verifyEmailError==='') {
                                        vehicle.email = event?.target?.value ?? '';
                                        vehicle = vehicle;
                                    }
                                    */

                                vehicle.email = event?.target?.value ?? '';
                                if (event?.target) {
                                    if (verifyEmailTimeout) {
                                        clearTimeout(verifyEmailTimeout);
                                        verifyEmailTimeout = null;
                                    }
                                    var value = (vehicle.email = event?.target.value);
                                    vehicle = vehicle;
                                    if (value) {
                                        if (z.string().email().safeParse(value).success === true) {
                                            verifyEmailTimeout = setTimeout(function () {
                                                verifyEmailError = '';
                                                verifyEmail(value);
                                            }, 500);
                                        } else {
                                            verifyEmailTimeout = setTimeout(function () {
                                                verifyEmailError = 'invalid-email';
                                            }, 500);
                                        }
                                    } else {
                                        verifyEmailError = 'empty-email';
                                    }
                                    error = verifyEmailError;
                                }
                            }}
                        />
                        {#if verifyEmailError}
                            <ErrorInline text={$_(verifyEmailError)} fontSize={12} />
                            <!--                            <span class="verify-email-error">{$_(verifyEmailError)}</span>-->
                        {/if}
                    </div>
                {:else}
                    <div
                        title={vehicle.email}
                        style="font-size: 14px; height: 100%; display: flex;  align-items: center;"
                    >
                        {#if isDemoVehicle}
                            <span style="opacity: 0.5">({$_('demo_account')})</span>
                        {:else if vehicle.is_group}
                            <span style="opacity: 0.5">({$_('vehicle_group_label')})</span>
                        {:else}
                            {(vehicle.email && vehicle.email) || '[email not set]'}
                        {/if}
                    </div>
                {/if}
            </div>
            <div class="warehouse">
                <div
                    style="display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start;  height: 100%;"
                >
                    <ErrorBounduary valid={VehicleValidator.isWarehouseIdValid(vehicleClass)}>
                        <SelectWarehouse
                            items={warehouses_items}
                            selected={(vehicle.warehouse_id && vehicle.warehouse_id.toString()) ||
                                ''}
                            on:change={event => {
                                if (event?.target) {
                                    vehicle.warehouse_id = parseInt(event?.target.value);
                                    vehicle = vehicle;
                                    if (!isNew) update(vehicleClass);
                                }
                            }}
                        />
                    </ErrorBounduary>
                </div>
            </div>
            <div class="expand-trigger">
                {#if !isNew}
                    <IconExpander
                        isOpen={expanded}
                        on:click={() => {
                            toggleExpanded();
                        }}
                    />
                {/if}
            </div>
        </div>

        {#if !(vehicle.is_group && isNew)}
            <div style="padding: 6px 10px;">
                <Switch label={$_('show_details')} bind:checked={expandedMore} />
            </div>
        {/if}
        {#if expandedMore && !(vehicle.is_group && isNew)}
            {#if vehicle.is_group && $features?.feature_vehicle_groups}
                <VehicleOneParagraph>
                    <div slot="title">Pojazdy</div>
                    <div slot="content">
                        <MultiSelect2
                            items={$vehiclesManager.getSelectItemsVehiclesOnly()}
                            selected={selectedVehiclesInGroupItems}
                            disabled={false}
                            placeholder={$_('brak pojazdów')}
                            allowUserOptions={false}
                            maxSelect={null}
                            onChange={selectedElements => {
                                if (vehicle.id === null) {
                                    console.warn('Vehicle id is null, cannot add to group');
                                    return;
                                }
                                console.log(
                                    'Set groups for vehicle: ',
                                    selectedElements.map(e => e.id)
                                );
                                (async () => {
                                    if (!vehicle.id) {
                                        return;
                                    }
                                    await $vehiclesManager.setVehiclesInGroup(
                                        vehicle.id, // group_id
                                        selectedElements.map(e => parseInt(e.id)) // vehicles
                                    );
                                    vehicleGroups = await VehiclesApi.getVehicleGroupItems(
                                        get(api)
                                    );
                                })();
                            }}
                        />
                    </div>
                </VehicleOneParagraph>
            {/if}

            {#if !vehicle.is_group && $features?.feature_vehicle_groups}
                <VehicleOneParagraph>
                    <div slot="title">Grupy</div>
                    <div slot="content">
                        <MultiSelect2
                            items={vehicleGroupItems}
                            selected={selectedVehicleGroupItems}
                            disabled={false}
                            placeholder={$_('brak grup')}
                            allowUserOptions={false}
                            maxSelect={null}
                            onChange={selectedElements => {
                                if (vehicle.id === null) {
                                    console.warn('Vehicle id is null, cannot add to group');
                                    return;
                                }
                                console.log(
                                    'Set groups for vehicle: ',
                                    selectedElements.map(e => e.id)
                                );
                                (async () => {
                                    if (!vehicle.id) {
                                        return;
                                    }
                                    await $vehiclesManager.setVehicleGroups(
                                        vehicle.id,
                                        selectedElements.map(e => parseInt(e.id))
                                    );
                                    vehicleGroups = await VehiclesApi.getVehicleGroupItems(
                                        get(api)
                                    );
                                })();
                            }}
                        />
                    </div>
                </VehicleOneParagraph>
            {/if}

            <VehicleOneParagraph>
                <div slot="title">{$_('Vehicle_status')}</div>
                <div slot="content">
                    {#if true}
                        <!-- hide due to not needed colors from vehicles -->
                        <VehicleOneSection>
                            <VehicleOneValue style="column-gap:0;">
                                <ColorSymbol
                                    color={vehicle.color ?? '#ffffff'}
                                    opts={{}}
                                    editable={true}
                                    on:change={onColorChange}
                                    scale={0.6}
                                    type={null}
                                    label={get(_)('Change_color')}
                                />
                                <!--                        <div class="link">Zmień kolor</div>-->
                            </VehicleOneValue>
                        </VehicleOneSection>
                    {/if}
                    {#if !vehicle.is_group}
                        <VehicleOneSection>
                            <VehicleOneLabel>
                                {$_('Vehicle_state')}
                            </VehicleOneLabel>
                            <VehicleOneValue>
                                <Checkbox
                                    label={$_('Vehicle_state_active')}
                                    checked={vehicle.enabled}
                                    onChange={() => {
                                        vehicle.enabled = !vehicle.enabled;
                                        if (!isNew) update(vehicleClass);
                                    }}
                                />
                            </VehicleOneValue>
                        </VehicleOneSection>
                    {/if}
                    {#if !vehicle.is_group}
                        <VehicleOneSection>
                            <VehicleOneLabel>
                                {$_('Vehicle_monitoring')}
                            </VehicleOneLabel>
                            <VehicleOneValue>
                                <Checkbox
                                    label={$_('Vehicle_monitor_position')}
                                    checked={vehicle.track}
                                    onChange={() => {
                                        vehicle.track = !vehicle.track;
                                        if (!isNew) update(vehicleClass);
                                    }}
                                />
                            </VehicleOneValue>
                        </VehicleOneSection>
                    {/if}
                    {#if !vehicle.is_group && $features?.feature_vehicle_ranking}
                        <VehicleOneSection>
                            <VehicleOneLabel>
                                {$_('Vehicle_ranking')}
                            </VehicleOneLabel>
                            <VehicleOneValue>
                                <VehicleRanking rankingPercent={vehicle.ranking_percent} />
                            </VehicleOneValue>
                        </VehicleOneSection>
                    {/if}
                </div>
            </VehicleOneParagraph>

            <VehicleOneParagraph>
                <div slot="title">{get(_)('Routes')}</div>
                <div slot="content">
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Vehicle_route_end')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            {#if available_vehicle_options.includes('routeend')}
                                <div>
                                    <!--                                    <div>{$_('Vehicle_route_attributes')}</div>-->
                                    <SearchForm
                                        placeholder={$_('address_or_coordinates')}
                                        pointOnMap={true}
                                        coords={vehicle.route_end?.coordinates || null}
                                        validSearchResult={null}
                                        label={vehicle.route_end?.query || ''}
                                        on:set={evt => {
                                            vehicle.route_end = {
                                                coordinates: evt.detail.coords,
                                                query: evt.detail.query,
                                            };
                                            centerMap(evt.detail.coords);
                                            mapObjectAdd();
                                            if (!isNew) update(vehicleClass);
                                        }}
                                        on:unset={() => {
                                            vehicle.route_end = null;
                                            mapObjectRemove();
                                            if (!isNew) update(vehicleClass);
                                        }}
                                    />
                                </div>
                            {/if}
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        {#if available_vehicle_options.includes('route')}
                            <div style="padding: 3px;">
                                <Checkbox
                                    label={$_('Vehicle_optimize_route')}
                                    checked={vehicle.route_optimize}
                                    onChange={() => {
                                        vehicle.route_optimize = !vehicle.route_optimize;
                                        if (!isNew) update(vehicleClass);
                                    }}
                                />
                            </div>
                        {/if}
                    </VehicleOneSection>
                </div>
            </VehicleOneParagraph>

            <VehicleOneParagraph>
                <div slot="title">{$_('Vehicle_load_limits')}</div>
                <div slot="content">
                    <ErrorBounduary
                        valid={VehicleValidator.isWeightLimitValid(vehicleClass) === '' &&
                            VehicleValidator.isParcelLimitValid(vehicleClass) === ''}
                        errors={[
                            VehicleValidator.isWeightLimitValid(vehicleClass),
                            VehicleValidator.isParcelLimitValid(vehicleClass),
                        ]}
                        withPadding={true}
                    >
                        {#if available_vehicle_options.includes('weight_limit')}
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_weight_limit')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        valid={VehicleValidator.isWeightLimitValid(vehicleClass) ===
                                            ''}
                                        value={vehicleClass.getWeightLimit()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle,
                                                'weight_limit',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                    {$_('Unit_Kilograms_Abbr')}
                                </VehicleOneValue>
                            </VehicleOneSection>
                        {/if}
                        {#if available_vehicle_options.includes('parcel_limit')}
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_parcel_limit')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        valid={VehicleValidator.isParcelLimitValid(vehicleClass) ===
                                            ''}
                                        value={vehicleClass.getParcelLimit()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle,
                                                'parcel_limit',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                </VehicleOneValue>
                            </VehicleOneSection>
                        {/if}
                    </ErrorBounduary>
                </div>
            </VehicleOneParagraph>

            {#if available_vehicle_options.includes('truck')}
                <VehicleOneParagraph>
                    <div slot="title">{$_('Vehicle_truck_parameters')}</div>
                    <div slot="content">
                        <ErrorBounduary
                            valid={VehicleValidator.isTruckParametersValid(vehicleClass)}
                            errors={getValidationTruckErrors(vehicleClass)}
                            withPadding={true}
                        >
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_total_weight')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        step="0.1"
                                        valid={VehicleValidator.isTruckTotalWeightValid(
                                            vehicleClass
                                        )}
                                        value={vehicleClass.getTruckTotalWeight()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle.truck_attributes,
                                                'weight_t',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                    {$_('Unit_Tone_Abbr')}
                                </VehicleOneValue>
                            </VehicleOneSection>
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_axis_load')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        valid={VehicleValidator.isTruckAxisLoadValid(vehicleClass)}
                                        step="0.1"
                                        value={vehicleClass.getTruckAxisLoad()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle.truck_attributes,
                                                'load_t',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                    {$_('Unit_Tone_Abbr')}
                                </VehicleOneValue>
                            </VehicleOneSection>
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_length')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        valid={VehicleValidator.isTruckLengthValid(vehicleClass)}
                                        step="0.1"
                                        value={vehicleClass.getTruckLength()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle.truck_attributes,
                                                'length_m',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                    {$_('Unit_Meters_Abbr')}
                                </VehicleOneValue>
                            </VehicleOneSection>
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_width')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        valid={VehicleValidator.isTruckWidthValid(vehicleClass)}
                                        step="0.1"
                                        value={vehicleClass.getTruckWidth()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle.truck_attributes,
                                                'width_m',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                    {$_('Unit_Meters_Abbr')}
                                </VehicleOneValue>
                            </VehicleOneSection>
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_height')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <InputNumber
                                        valid={VehicleValidator.isTruckHeightValid(vehicleClass)}
                                        step="0.1"
                                        value={vehicleClass.getTruckHeight()?.toString() ?? ''}
                                        on:change={event => {
                                            handleInputNumberChange(
                                                vehicle.truck_attributes,
                                                'height_m',
                                                event?.target,
                                                vehicleClass
                                            );
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                    {$_('Unit_Meters_Abbr')}
                                </VehicleOneValue>
                            </VehicleOneSection>
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_category')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <ExtendedSelect
                                        value={(vehicleClass.getTruckCategory() ?? null)}
                                        enterValue={false}
                                        options={[
                                            {value: null, label: '-'},
                                            {value: 2, selectedLabel: '2', label: $_('Vehicle_category_2')},
                                            {value: 3, selectedLabel: '3', label: $_('Vehicle_category_3')},
                                            {value: 4, selectedLabel: '4', label: $_('Vehicle_category_4')},
                                            {value: 5, selectedLabel: '5', label: $_('Vehicle_category_5')},
                                        ]}
                                        inputWidth={30}
                                        showArrow={true}
                                        on:change={event => {
                                            vehicle.truck_attributes.category = event.detail;
                                            update(vehicleClass);
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                </VehicleOneValue>
                            </VehicleOneSection>
                            <VehicleOneSection>
                                <VehicleOneLabel>
                                    {$_('Vehicle_euronorm')}
                                </VehicleOneLabel>
                                <VehicleOneValue>
                                    <ExtendedSelect
                                        value={(vehicleClass.getTruckEuroNorm() ?? null)}
                                        enterValue={false}
                                        options={[
                                            {value: null, label: '-'},
                                            {value: 2, label: '2'},
                                            {value: 3, label: '3'},
                                            {value: 4, label: '4'},
                                            {value: 5, label: '5'},
                                            {value: 6, label: '6'}
                                        ]}
                                        inputWidth={30}
                                        showArrow={true}
                                        on:change={event => {
                                            vehicle.truck_attributes.euro_norm = event.detail;
                                            update(vehicleClass);
                                            vehicle = vehicle;
                                        }}
                                        style="max-width:80px;"
                                    />
                                </VehicleOneValue>
                            </VehicleOneSection>
                        </ErrorBounduary>
                    </div>
                </VehicleOneParagraph>
            {/if}

            <VehicleOneParagraph>
                <div slot="title">{$_('Route cost params')}</div>
                <div slot="content">
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Each route')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <InputNumber
                                valid={VehicleValidator.isRouteCostParamValid(vehicleClass, 'per_init')}
                                step="0.1"
                                value={vehicleClass.getRouteCost('per_init')?.toString() ?? ''}
                                on:change={event => {
                                    handleInputNumberChange(
                                        vehicle.route_cost,
                                        'per_init',
                                        event?.target,
                                        vehicleClass,
                                        true
                                    );
                                    vehicle = vehicle;
                                }}
                                style="max-width:80px;"
                            />
                            {$customerSettings.route_cost.unit}
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Each item')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <InputNumber
                                valid={VehicleValidator.isRouteCostParamValid(vehicleClass, 'per_item')}
                                step="0.1"
                                value={vehicleClass.getRouteCost('per_item')?.toString() ?? ''}
                                on:change={event => {
                                    handleInputNumberChange(
                                        vehicle.route_cost,
                                        'per_item',
                                        event?.target,
                                        vehicleClass,
                                        true
                                    );
                                    vehicle = vehicle;
                                }}
                                style="max-width:80px;"
                            />
                            {$customerSettings.route_cost.unit}
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Each minute')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <InputNumber
                                valid={VehicleValidator.isRouteCostParamValid(vehicleClass, 'per_min')}
                                step="0.1"
                                value={vehicleClass.getRouteCost('per_min')?.toString() ?? ''}
                                on:change={event => {
                                    handleInputNumberChange(
                                        vehicle.route_cost,
                                        'per_min',
                                        event?.target,
                                        vehicleClass,
                                        true
                                    );
                                    vehicle = vehicle;
                                }}
                                style="max-width:80px;"
                            />
                            {$customerSettings.route_cost.unit}
                        </VehicleOneValue>
                    </VehicleOneSection>
                    <VehicleOneSection>
                        <VehicleOneLabel>
                            {$_('Each kilometer')}
                        </VehicleOneLabel>
                        <VehicleOneValue>
                            <InputNumber
                                valid={VehicleValidator.isRouteCostParamValid(vehicleClass, 'per_km')}
                                step="0.1"
                                value={vehicleClass.getRouteCost('per_km')?.toString() ?? ''}
                                on:change={event => {
                                    handleInputNumberChange(
                                        vehicle.route_cost,
                                        'per_km',
                                        event?.target,
                                        vehicleClass,
                                        true
                                    );
                                    vehicle = vehicle;
                                }}
                                style="max-width:80px;"
                            />
                            {$customerSettings.route_cost.unit}
                        </VehicleOneValue>
                    </VehicleOneSection>
                </div>
            </VehicleOneParagraph>

            <VehicleOneParagraph>
                <div slot="title">{$_('Vehicle_working_hours')}</div>
                <div slot="content">
                    <SelectOpenHours
                        bind:openHours={vehicle.open_hours}
                        onChange={() => {
                            if (!isNew) update(vehicleClass);
                        }}
                    />
                </div>
            </VehicleOneParagraph>

            {#if !vehicle.is_group}
                <VehicleOneParagraph>
                    <div slot="title">{$_('Vehicle_productivity_factor')}</div>
                    <div slot="content">
                        <TelematicsSliderSingle
                            props={{
                                currentValue: vehicle.open_hours_adjust_percent,
                                sliderWidth: 250,
                            }}
                            onChange={currentValue => {
                                vehicle.open_hours_adjust_percent = currentValue;
                                if (!isNew) update(vehicleClass);
                            }}
                        />
                    </div>
                </VehicleOneParagraph>
            {/if}

            {#if !vehicle.is_group}
                <VehicleOneParagraph>
                    <div slot="title">
                        {$_('License plate number')}
                    </div>
                    <div slot="content">
                        <InputText
                            value={vehicle.license_plate_number}
                            on:change={e => {
                                vehicle.license_plate_number = e.target?.value ?? '';
                                if (!isNew) update(vehicleClass);
                            }}
                        />
                    </div>
                </VehicleOneParagraph>
            {/if}

            <VehicleOneParagraph>
                <div slot="title">
                    {$_('Description')}
                </div>
                <div slot="content">
                    {#if !showDescriptionField}
                        <Label
                            on:click={() => {
                                showDescriptionField = true;
                            }}>{$_('Add_description')}</Label
                        >
                    {:else}
                        <Textarea
                            value={vehicle.description}
                            onChange={v => {
                                console.log(v);
                                vehicle.description = v;
                                if (!isNew) update(vehicleClass);
                            }}
                        />
                    {/if}
                </div>
            </VehicleOneParagraph>
        {/if}

        {#if !isNew}
            <div style="padding: 20px;">
                {#if vehicle.is_group}
                    <ButtonRemove
                        on:click={removeVehicleWithConfirm}
                        text={$_('remove_vehicle_group')}
                    />
                {:else}
                    <ButtonRemove on:click={removeVehicleWithConfirm} text={$_('remove_vehicle')} />
                {/if}
                {#if error}
                    <div
                        style="width: 85%; display: flex; justify-content: flex-end; padding: 0 20px 5px 20px;"
                    >
                        <!--                            <MessageDangerSmall message={$_(error)} />-->
                        <ErrorInline text={$_(error)} />
                    </div>
                {/if}
            </div>
        {/if}

        {#if isNew}
            <div
                style="display: flex; justify-content: flex-end; align-items: flex-end; padding: 20px;"
            >
                {#if error}
                    <div
                        style="width: 85%; display: flex; justify-content: flex-end; padding: 0 20px 5px 20px;"
                    >
                        <!--                            <MessageDangerSmall message={$_(error)} />-->
                        <ErrorInline text={$_(error)} />
                    </div>
                {/if}
                <div style="display: flex; align-items: center;column-gap: 30px;">
                    <span style="font-size: 14px;">
                        <LinkSmallBlack
                            text={$_('Cancel')}
                            on:click={() => {
                                addMode = false;
                            }}
                        />
                    </span>
                    <ButtonMain
                        text={$_('Add vehicle')}
                        disabled={loading || !validated}
                        on:click={() => {
                            error = '';
                            loading = true;
                            VehiclesApi.createOne(get(api), vehicle)
                                .then(vehicle => {
                                    onUpdateOne(true);
                                    vehicle = JSON.parse(JSON.stringify(vehicleOriginal));
                                })
                                .catch(err => {
                                    error = err;
                                })
                                .finally(() => {
                                    loading = false;
                                });
                        }}
                    />
                </div>
            </div>
        {/if}
    </div>
{/if}

<style>
    .vehicle-header {
        display: grid;
        grid-template-columns: 30px 1fr 30px 1fr 1fr 30px;
        /*grid-template-rows: 41px;*/
        line-height: 20px;
        padding: 2px 0;
        /*background-color: #cfa516;*/
        grid-template-areas: 'vehicle_icon name active email warehouse expand-trigger';
    }
    .vehicle-header.with-select-control {
        grid-template-columns: 30px 30px 1fr 30px 1fr 1fr 30px;
        grid-template-areas: 'select_control vehicle_icon name active email warehouse expand-trigger';
    }
    .vehicle-header .vehicle_icon {
        grid-area: vehicle_icon;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: 2px 5px;
    }
    .vehicle-header .name {
        grid-area: name;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding: 2px 5px;
        cursor: pointer;
        overflow: hidden;
    }
    .vehicle-header .active {
        grid-area: active;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .vehicle-header .email {
        grid-area: email;
        display: flex;
        align-items: center;
        padding: 2px 5px;
        cursor: pointer;
        overflow: hidden;
    }
    .vehicle-header .warehouse {
        grid-area: warehouse;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding: 2px 5px;
        cursor: pointer;
        /*overflow: hidden;*/
    }
    .vehicle-header .expand-trigger {
        grid-area: expand-trigger;
        display: flex;
        justify-content: center;
        cursor: pointer;
        align-items: flex-start;
        padding: 2px 5px;
    }

    .vehicle-expanded-container {
        background-color: var(--selected-item-background);
        /*background-color: #f92672;*/
    }
    .vehicle-expanded-container .vehicle-header {
        /*grid-template-rows: 41px;*/
        grid-template-rows: min-content;
        padding: 2px 0;
    }
    /*.link {*/
    /*    color: #1d5fc9;*/
    /*    cursor: pointer;*/
    /*    text-align: center;*/
    /*}*/
</style>
